import React, { useRef, useState, useCallback, useMemo } from 'react'
import {
    TextField,
    IconButton,
    Tooltip,
    LinearProgress,
    Typography,
    Box,
    Theme,
} from '@material-ui/core'
import SendIcon from '@material-ui/icons/Send'
import MicIcon from '@material-ui/icons/Mic'
import { makeStyles } from '@material-ui/core/styles'
import debounce from 'lodash/debounce'

interface ChatInputProps {
    userInput: string
    setUserInput: (value: string) => void
    handleSendMessage: (input: string, activeURL: string) => void
    connectionReady: boolean
    progress: number
    recording: boolean
    startRecording: () => void
    stopRecording: () => void
    theme: Theme
    activeURL: string
}

const useStyles = makeStyles((theme: Theme) => ({
    micButton: {
        position: 'relative',
        zIndex: 1,
    },
    micPulse: {
        position: 'absolute',
        top: -8,
        left: -8,
        width: 48,
        height: 48,
        borderRadius: '50%',
        background: 'rgba(255,0,0,0.2)',
        boxShadow: '0 0 0 0 rgba(255,0,0,0.7)',
        animation: '$pulse 1.2s infinite',
        zIndex: 0,
    },
    '@keyframes pulse': {
        '0%': {
            transform: 'scale(1)',
            opacity: 1,
        },
        '70%': {
            transform: 'scale(1.5)',
            opacity: 0.3,
        },
        '100%': {
            transform: 'scale(2)',
            opacity: 0,
        },
    },
}))

export const ChatInput: React.FC<ChatInputProps> = React.memo(
    ({
        userInput,
        setUserInput,
        handleSendMessage,
        connectionReady,
        progress,
        recording,
        startRecording,
        stopRecording,
        theme,
        activeURL,
    }) => {
        const classes = useStyles()
        const [localInput, setLocalInput] = useState(userInput)

        // Sync local state when userInput prop changes (e.g., when cleared after sending)
        React.useEffect(() => {
            setLocalInput(userInput)
        }, [userInput])

        // Debounce the input update
        const debouncedSetUserInput = useMemo(
            () =>
                debounce((value: string) => {
                    setUserInput(value)
                }, 100),
            [setUserInput],
        )

        // Memoize handlers to prevent recreation on each render
        const handleInputChange = useCallback(
            (e: React.ChangeEvent<HTMLInputElement>) => {
                const value = e.target.value
                // Update local state immediately for responsiveness
                setLocalInput(value)
                // Debounce the parent state update
                debouncedSetUserInput(value)
            },
            [debouncedSetUserInput],
        )

        const handleKeyDown = useCallback(
            (e: React.KeyboardEvent) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    handleSendMessage(localInput, activeURL)
                }
            },
            [handleSendMessage, localInput, activeURL],
        )

        const handleSendClick = useCallback(() => {
            handleSendMessage(localInput, activeURL)
        }, [handleSendMessage, localInput, activeURL])
        return (
            <>
                {connectionReady ? (
                    <>
                        <TextField
                            variant="outlined"
                            fullWidth
                            placeholder="Ask about a notification"
                            style={{ marginRight: theme.spacing(1) }}
                            value={localInput}
                            onChange={handleInputChange}
                            onKeyDown={handleKeyDown}
                            inputProps={{
                                style: { caretColor: 'auto' }, // Ensure cursor is visible
                            }}
                        />
                        {/* Send Button */}
                        <Tooltip title="Send Message">
                            <IconButton onClick={handleSendClick}>
                                <SendIcon
                                    htmlColor={theme.palette.text.secondary}
                                />
                            </IconButton>
                        </Tooltip>
                        {/* Mic Button */}
                        <Tooltip title="Record Message">
                            <span
                                style={{
                                    position: 'relative',
                                    display: 'inline-block',
                                }}
                            >
                                <IconButton
                                    className={classes.micButton}
                                    onClick={
                                        recording
                                            ? stopRecording
                                            : startRecording
                                    }
                                    aria-label={
                                        recording
                                            ? 'Stop recording'
                                            : 'Start recording'
                                    }
                                    style={{
                                        backgroundColor: recording
                                            ? '#ffebee'
                                            : undefined,
                                        border: recording
                                            ? '2px solid #f44336'
                                            : undefined,
                                    }}
                                >
                                    {recording && (
                                        <span className={classes.micPulse} />
                                    )}
                                    <MicIcon
                                        htmlColor={
                                            recording
                                                ? '#f44336'
                                                : theme.palette.text.secondary
                                        }
                                    />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </>
                ) : (
                    <Box width="100%">
                        <Typography variant="body2" align="center" gutterBottom>
                            Connecting to Lori
                        </Typography>
                        <LinearProgress
                            variant="determinate"
                            value={progress}
                        />
                    </Box>
                )}
            </>
        )
    },
)
